import axios from 'axios';
import router from '../router';

function apiBase() {
  return process.env.VUE_APP_API_URL || 'http://127.0.0.1:3000/api';
}

function authHeaders() {
  const token = localStorage.getItem('userToken');
  if (!token) {
    router.push({ name: 'login' });
    return null;
  }
  return { Authorization: `Bearer ${token}` };
}

const state = {
  categories: [],
};

const mutations = {
  SET_CATEGORIES(state, categories) {
    state.categories = categories;
  },
  SET_CATEGORY_ORDER(state, categories) {
    state.categories = categories;
  },
  UPDATE_CATEGORY_ACTIVE(state, { categoryId, isActive }) {
    const cat = state.categories.find(c => c.categoryId === categoryId);
    if (cat) cat.is_active = isActive ? 1 : 0;
  },
  REMOVE_CATEGORIES(state, categoryIds) {
    state.categories = state.categories.filter(
      c => !categoryIds.includes(c.categoryId)
    );
  },
};

const actions = {
  async fetchCategories({ commit }, { search = '' }) {
    const headers = authHeaders();
    if (!headers) return;
    try {
      const response = await axios.get(`${apiBase()}/categories`, {
        params: { search, limit: 200 },
        headers,
      });
      const sorted = [...response.data].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      commit('SET_CATEGORIES', sorted);
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  async createCategory({ dispatch }, categoryData) {
    const headers = authHeaders();
    if (!headers) return;
    try {
      await axios.post(`${apiBase()}/categories`, categoryData, { headers });
      await dispatch('fetchCategories', { search: '' });
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  },

  async updateCategory({ dispatch }, categoryData) {
    const headers = authHeaders();
    if (!headers) return;
    try {
      await axios.put(`${apiBase()}/categories/${categoryData.categoryId}`, categoryData, { headers });
      await dispatch('fetchCategories', { search: '' });
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  },

  async toggleCategoryActive({ commit }, { categoryId, isActive }) {
    const headers = authHeaders();
    if (!headers) return;
    // Optimistic update
    commit('UPDATE_CATEGORY_ACTIVE', { categoryId, isActive });
    try {
      await axios.patch(
        `${apiBase()}/categories/${categoryId}/active`,
        { isActive },
        { headers }
      );
    } catch (error) {
      // Revert on failure
      commit('UPDATE_CATEGORY_ACTIVE', { categoryId, isActive: !isActive });
      console.error('Error toggling category active:', error);
      throw error;
    }
  },

  async deleteCategory({ dispatch }, categoryId) {
    const headers = authHeaders();
    if (!headers) return;
    try {
      await axios.delete(`${apiBase()}/categories/${categoryId}`, { headers });
      await dispatch('fetchCategories', { search: '' });
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  },

  /**
   * Delete several categories.
   *
   * Two-step by design. Called without reassignTo, the server refuses
   * with 409 if any of the categories still contain products and tells
   * us which and how many. The caller shows that ("3 categories contain
   * 47 products — move them to Standard?") and calls again with the
   * chosen target. Nothing is deleted until the user has seen the
   * number.
   *
   * NOT optimistic, unlike toggleCategoryActive above. An optimistic
   * removal is a promise that the call will succeed, and this one is
   * allowed to be refused — a 409 is an answer, not a failure. It used
   * to remove the rows up front and POST to a route that did not exist:
   * the list emptied, the 404 went to the console, and a refresh
   * brought everything back. Rows are removed only once the server has
   * named which ids it actually deleted.
   *
   * Does not throw on 409 for the same reason. A question thrown as an
   * exception gets handled as an error somewhere up the stack.
   *
   * @param {number[]} categoryIds
   * @param {number|null} reassignTo  where to move products first
   * @returns {{ok: boolean, deleted?, moved?, skipped?, conflict?, error?}}
   */
  async bulkDeleteCategories({ commit }, payload) {
    const headers = authHeaders();
    if (!headers) return { ok: false, error: 'Not authenticated' };

    // Accepts a bare array as well as { categoryIds, reassignTo } — the
    // old call site passed an array, and a signature change should not
    // be what breaks it.
    const categoryIds = Array.isArray(payload) ? payload : payload.categoryIds;
    const reassignTo  = Array.isArray(payload) ? null : (payload.reassignTo ?? null);

    try {
      const { data } = await axios.post(
        `${apiBase()}/categories/bulk-delete`,
        { categoryIds, reassignTo },
        { headers },
      );

      // Only the ids the server confirmed. Keeps state consistent when
      // some were skipped — the default category, or ids that were
      // already gone.
      commit('REMOVE_CATEGORIES', data.deletedIds || []);

      return {
        ok:      true,
        deleted: data.deleted || 0,
        moved:   data.moved   || 0,
        skipped: data.skipped || [],
      };
    } catch (error) {
      const response = error.response;

      // 409 = the server is asking a question, not reporting a fault.
      // Hand the details back so the dialog can offer a target.
      if (response && response.status === 409) {
        return { ok: false, conflict: response.data };
      }

      console.error('Error bulk deleting categories:', error);
      return {
        ok: false,
        error: response?.data?.error || error.message || 'Bulk delete failed',
      };
    }
  },

  async reorderCategories({ commit }, newOrder) {
    const headers = authHeaders();
    if (!headers) return;
    commit('SET_CATEGORY_ORDER', newOrder);
    const orderPayload = newOrder.map((cat, index) => ({
      categoryId: cat.categoryId,
      sortOrder:  index + 1,
    }));
    try {
      await axios.put(`${apiBase()}/categories/reorder`, { order: orderPayload }, { headers });
    } catch (error) {
      console.error('Error saving category order:', error);
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};